import React, { useCallback, useState } from 'react';

import dayjs from '~/timeInit';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import * as libRegions from '@luxuryescapes/lib-regions';

import NewScheduleForm from '~/components/Common/Forms/NewScheduleForm';

import { brands } from '~/consts/brands';
import { AVAILABILITY, ONLINE_PURCHASE } from '~/consts/schedule';
import { types as outerScheduleTypes } from '~/consts/types';

import { formatDateShort, toSortableTimestamp } from '~/services/TimeService';

import { scheduleForType } from '~/utils/scheduleForType';

import SortableTableHeaderCell from '../../shared/SortableTableHeaderCell';
import { useTableSort } from '../../shared/useTableSort';

import { CampaignScheduleDropdownRow } from './CampaignScheduleExpandableRow';
import CampaignScheduleValuePillCell from './CampaignScheduleValuePillCell';
import {
  CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT,
  handleExpandRowKeyDown,
  toggleExpandedRow,
} from './campaignScheduleTabLayout';
import type { BrandSchedule, CampaignScheduleParentRow } from './types';

type CampaignScheduleSortField =
  | 'types'
  | 'startOnline'
  | 'endOnline'
  | 'startOffline'
  | 'endOffline'
  | 'brands'
  | 'markets';

// All brands / all regions — Book By Dates and Available Offline are offer-wide concepts (they
// bound the List Visibility/LuxPlus+ windows across every brand and region), matching how the
// full edit-offer/schedules page's overall Book By / Available Offline inputs behave. Editing
// here updates every existing schedule of that type for the whole offer at once.
const ALL_BRAND_VALUES = brands.map((brand) => brand.value);
const ALL_REGIONS = libRegions.getRegions();

const SET_ASIDE_ROW_SX = {
  '& .MuiTableCell-body, & .MuiTableCell-body .MuiTypography-root': { color: 'text.disabled' },
};

function setAsideChip(parentRow: { isSuperseded?: boolean; isUpcoming?: boolean }) {
  if (parentRow.isSuperseded) {
    return {
      label: 'Superseded',
      tooltip: "An earlier cycle of a re-opened campaign — a later cycle replaces it, so it won't be imported",
    };
  }
  if (parentRow.isUpcoming) {
    return {
      label: 'Upcoming',
      tooltip: 'A later cycle of a re-opened campaign — import it once the current cycle ends',
    };
  }
  return null;
}

interface DropdownTriggerCellProps {
  label: string;
  values: Array<string>;
  isExpanded: boolean;
  onToggle: () => void;
}

// Mirrors the Site Schedules table's "Regions" cell: single values are shown plainly, multiple
// values collapse into a count that expands a row below (via CampaignScheduleDropdownRow) instead
// of growing the cell inline.
function DropdownTriggerCell({ label, values, isExpanded, onToggle }: DropdownTriggerCellProps) {
  if (values.length <= 1) {
    return <Typography variant="body2">{values[0] ?? '—'}</Typography>;
  }

  return (
    <Stack
      alignItems="center"
      direction="row"
      role="button"
      spacing={0.5}
      sx={{ cursor: 'pointer' }}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(event) => handleExpandRowKeyDown(event, onToggle)}
    >
      <Typography variant="body2">{`${values.length} ${label}`}</Typography>
      {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
    </Stack>
  );
}

interface Props {
  brandSchedules: Array<BrandSchedule>;
  parentRows: Array<CampaignScheduleParentRow>;
  offerId?: string;
}

export default function ImportedSalesforceDatesSection({ brandSchedules, parentRows, offerId }: Props) {
  const [expandedBrandRows, setExpandedBrandRows] = useState<Set<string>>(new Set());
  const [expandedRegionRows, setExpandedRegionRows] = useState<Set<string>>(new Set());
  // Shared across both forms, matching the full edit-offer/schedules page: saving one disables
  // the other, since Available Offline's date range is constrained by Book By Dates below.
  const [isEditingOuterSchedule, setIsEditingOuterSchedule] = useState(false);
  const availabilityStart = scheduleForType(brandSchedules, AVAILABILITY, 'start');
  const availabilityEnd = scheduleForType(brandSchedules, AVAILABILITY, 'end');
  const getSortValue = useCallback((row: CampaignScheduleParentRow, field: CampaignScheduleSortField) => {
    switch (field) {
      case 'types':
        return row.campaignTypes.join(', ');
      case 'startOnline':
        return toSortableTimestamp(row.startOnline);
      case 'endOnline':
        return toSortableTimestamp(row.endOnline);
      case 'startOffline':
        return toSortableTimestamp(row.startOffline);
      case 'endOffline':
        return toSortableTimestamp(row.endOffline);
      case 'brands':
        return row.brands.join(', ');
      case 'markets':
        return row.markets.join(', ');
      default:
        return null;
    }
  }, []);
  const {
    sortField,
    sortDirection,
    handleSortChange,
    sortedRows: sortedParentRows,
  } = useTableSort(parentRows, getSortValue, 'startOnline');

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
      <Typography fontWeight={700} sx={{ mb: 2 }} variant="subtitle1">
        Imported Salesforce Dates
      </Typography>
      <Stack direction="column" spacing={2}>
        {outerScheduleTypes.map((type) => (
          <Paper key={type.code} variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Typography fontWeight={700} gutterBottom variant="subtitle2">
              {type.name}
            </Typography>
            {offerId ? (
              <NewScheduleForm
                idList={brandSchedules.filter((schedule) => schedule.type === type.code).map((s) => s.id)}
                offerId={offerId}
                brands={ALL_BRAND_VALUES}
                disabled={isEditingOuterSchedule}
                onSave={() => setIsEditingOuterSchedule(true)}
                onSuccess={() => setIsEditingOuterSchedule(false)}
                type={type.code}
                regions={ALL_REGIONS}
                start={scheduleForType(brandSchedules, type.code, 'start')}
                end={scheduleForType(brandSchedules, type.code, 'end')}
                minDate={type.code === ONLINE_PURCHASE && availabilityStart ? dayjs(availabilityStart) : undefined}
                maxDate={type.code === ONLINE_PURCHASE && availabilityEnd ? dayjs(availabilityEnd) : undefined}
                fullWidth
              />
            ) : (
              <Typography color="text.secondary" variant="body2">
                Offer must be saved before dates can be edited.
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>

      <TableContainer sx={{ maxWidth: '100%', mt: 3, overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}>Salesforce Campaign Schedules</TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <SortableTableHeaderCell
                field="types"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Type
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="startOnline"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Start Online
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="endOnline"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                End Online
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="startOffline"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Start Offline
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="endOffline"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                End Offline
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="brands"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Brand
              </SortableTableHeaderCell>
              <SortableTableHeaderCell
                field="markets"
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              >
                Region
              </SortableTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parentRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce campaign schedules found for this offer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sortedParentRows.map((parentRow) => {
              const isBrandExpanded = expandedBrandRows.has(parentRow.id);
              const isRegionExpanded = expandedRegionRows.has(parentRow.id);
              const setAside = setAsideChip(parentRow);

              return (
                <React.Fragment key={parentRow.id}>
                  <TableRow sx={setAside ? SET_ASIDE_ROW_SX : undefined}>
                    <TableCell>
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <CampaignScheduleValuePillCell label="types" values={parentRow.campaignTypes} />
                        {setAside && (
                          <Tooltip title={setAside.tooltip}>
                            <Chip
                              label={setAside.label}
                              size="small"
                              sx={{ bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12), fontWeight: 600 }}
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{parentRow.startOnline ? formatDateShort(parentRow.startOnline) : '—'}</TableCell>
                    <TableCell>{parentRow.endOnline ? formatDateShort(parentRow.endOnline) : '—'}</TableCell>
                    <TableCell>{parentRow.startOffline ? formatDateShort(parentRow.startOffline) : '—'}</TableCell>
                    <TableCell>{parentRow.endOffline ? formatDateShort(parentRow.endOffline) : '—'}</TableCell>
                    <TableCell>
                      <DropdownTriggerCell
                        isExpanded={isBrandExpanded}
                        label="brands"
                        values={parentRow.brands}
                        onToggle={() => setExpandedBrandRows((current) => toggleExpandedRow(current, parentRow.id))}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownTriggerCell
                        isExpanded={isRegionExpanded}
                        label="regions"
                        values={parentRow.markets}
                        onToggle={() => setExpandedRegionRows((current) => toggleExpandedRow(current, parentRow.id))}
                      />
                    </TableCell>
                  </TableRow>
                  <CampaignScheduleDropdownRow
                    colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}
                    isExpanded={isBrandExpanded}
                  >
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {parentRow.brands.map((brand) => (
                        <Chip key={brand} label={brand} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </CampaignScheduleDropdownRow>
                  <CampaignScheduleDropdownRow
                    colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}
                    isExpanded={isRegionExpanded}
                  >
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {parentRow.markets.map((region) => (
                        <Chip key={region} label={region} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </CampaignScheduleDropdownRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
